import { useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Snackbar from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  maintenanceBannerDismissed,
  selectMaintenanceBannerDismissed,
  selectScheduledMaintenance,
} from 'src/v3/settings/settings.slice'
import type { MaintenanceTimerState } from './use-maintenance-timer'

let formatScheduledTime = (isoString: string): string =>
  dayjs(isoString).format('ddd, MMM D [at] h:mm A')

let formatTimeRemaining = (minutesUntil: number): string => {
  let h = Math.floor(minutesUntil / 60)
  let m = Math.floor(minutesUntil % 60)
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

type MaintenanceBannerProps = {
  timer: MaintenanceTimerState
}

export let MaintenanceBanner = ({ timer }: MaintenanceBannerProps) => {
  let dispatch = useAppDispatch()
  let maintenance = useAppSelector(selectScheduledMaintenance)
  let dismissed = useAppSelector(selectMaintenanceBannerDismissed)
  let { tier, minutesUntil, countdownText } = timer

  if (!maintenance || tier === 'none') return null
  if (tier === 'warning' && dismissed) return null

  if (tier === 'warning') {
    return (
      <Alert
        severity="warning"
        onClose={() => dispatch(maintenanceBannerDismissed())}
        sx={{ mb: 2 }}
      >
        <strong>Scheduled Maintenance</strong> on{' '}
        {formatScheduledTime(maintenance.startTime)} — in{' '}
        <strong>{formatTimeRemaining(minutesUntil)}</strong>
        {maintenance.message ? `. ${maintenance.message}` : ''}
      </Alert>
    )
  }

  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
      >
        <Typography component="span" variant="body2">
          <strong>Maintenance begins in</strong>
        </Typography>
        <Typography
          component="span"
          sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1rem' }}
        >
          {countdownText}
        </Typography>
        <Typography component="span" variant="body2">
          Please save your work.
        </Typography>
      </Box>
    </Alert>
  )
}

type MaintenanceToastProps = {
  timer: MaintenanceTimerState
}

// Fires a one-time toast when minutesUntil first drops to or below 30.
export let MaintenanceToast = ({ timer }: MaintenanceToastProps) => {
  let maintenance = useAppSelector(selectScheduledMaintenance)
  let { minutesUntil } = timer
  let [open, setOpen] = useState(false)
  let hasShownRef = useRef(false)

  useEffect(() => {
    if (!hasShownRef.current && minutesUntil > 0 && minutesUntil <= 30) {
      setOpen(true)
      // eslint-disable-next-line fp/no-mutation
      hasShownRef.current = true
    }
  }, [minutesUntil])

  if (!maintenance) return null

  return (
    <Snackbar
      open={open}
      onClose={() => setOpen(false)}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="warning" onClose={() => setOpen(false)} variant="filled">
        Scheduled maintenance begins in{' '}
        <strong>{Math.ceil(minutesUntil)} minutes</strong>. Please save your
        work.
      </Alert>
    </Snackbar>
  )
}
